import {
	createActionsHook,
	createStateHook,
	createStore,
	type BoundActions,
	type HookActionsFunction,
	type HookStateFunction,
	type StoreActionApi,
} from 'react-sweet-state';

/**
 * Shared store for the third-party connector setup modal.
 *
 * The modal open state is held in a module-level react-sweet-state store so it
 * is decoupled from where the modal is rendered. Multiple surfaces (sidebar,
 * nav search) can call `openSetupModal` to request the modal, while a single
 * always-mounted host (ConnectorSetupModalHost) reads `activeConnectorKey` and
 * renders the modal. This avoids the modal being torn down when the surface
 * that triggered it unmounts (e.g. the QuickFind search dialog closing).
 */
type SetupConnectorState = {
	activeConnectorKey: string | null;
	/**
	 * Connector keys that have been connected in this session. Held here so the
	 * just-connected connector can be removed from the recommended list
	 * immediately, without waiting for the admin_getAllDataSources query
	 * (useConnectorStates) to refetch.
	 */
	connectedConnectorKeys: string[];
};

const initialState: SetupConnectorState = {
	activeConnectorKey: null,
	connectedConnectorKeys: [],
};

const actions = {
	openSetupModal:
		(connectorKey: string) =>
		({ setState }: StoreActionApi<SetupConnectorState>): void => {
			setState({ activeConnectorKey: connectorKey });
		},
	closeModal:
		() =>
		({ setState }: StoreActionApi<SetupConnectorState>): void => {
			setState({ activeConnectorKey: null });
		},
	markConnectorConnected:
		(connectorKey: string) =>
		({ getState, setState }: StoreActionApi<SetupConnectorState>): void => {
			const { connectedConnectorKeys } = getState();
			if (connectedConnectorKeys.includes(connectorKey)) {
				return;
			}
			setState({ connectedConnectorKeys: [...connectedConnectorKeys, connectorKey] });
		},
};

const SetupConnectorStore = createStore({
	initialState,
	actions,
	name: 'Twcg1489SetupConnectorStore',
});

/**
 * Actions-only hook (`openSetupModal`, `closeModal`) for the setup-connector
 * store. The returned actions are stable references, so consuming this instead
 * of `useSetupConnector` avoids subscribing to `activeConnectorKey`. Use this in
 * components that only trigger the modal (e.g. the surfaces via
 * `useConnectorPromoConfig`) so they do not re-render on every modal open/close.
 */
export const useSetupConnectorActions: HookActionsFunction<
	BoundActions<
		SetupConnectorState,
		{
			openSetupModal: (
				connectorKey: string,
			) => ({ setState }: StoreActionApi<SetupConnectorState>) => void;
			closeModal: () => ({ setState }: StoreActionApi<SetupConnectorState>) => void;
			markConnectorConnected: (
				connectorKey: string,
			) => ({ getState, setState }: StoreActionApi<SetupConnectorState>) => void;
		}
	>
> = createActionsHook(SetupConnectorStore);

const useActiveConnectorKey = createStateHook(SetupConnectorStore, {
	selector: (state: SetupConnectorState) => state.activeConnectorKey,
});

/**
 * State hook returning the connector keys connected during this session. Use in
 * components that need to react to a connection completing (e.g. to remove the
 * connector from the recommended list immediately) without subscribing to the
 * modal open state.
 */
export const useConnectedConnectorKeys: HookStateFunction<string[], void> = createStateHook(
	SetupConnectorStore,
	{
		selector: (state: SetupConnectorState) => state.connectedConnectorKeys,
	},
);

/**
 * Hook to manage the third-party connector setup modal state.
 *
 * Returns `openSetupModal` to set the active connector key,
 * `activeConnectorKey` to determine which connector modal to show,
 * and `closeModal` to dismiss it. State is shared across all callers via a
 * module-level store, so the modal can be opened from one surface and rendered
 * by a separate always-mounted host (ConnectorSetupModalHost).
 *
 * This subscribes to `activeConnectorKey`; components that only need to trigger
 * the modal should use `useSetupConnectorActions` to avoid re-rendering on every
 * modal open/close.
 */
export function useSetupConnector(): {
	openSetupModal: (connectorKey: string) => void;
	activeConnectorKey: string | null;
	closeModal: () => void;
} {
	const activeConnectorKey = useActiveConnectorKey();
	const { openSetupModal, closeModal } = useSetupConnectorActions();

	return { openSetupModal, activeConnectorKey, closeModal };
}
